import * as React from 'react';
import { FaPlus, FaDownload, FaUpload } from 'react-icons/fa';
import LocationFilter from './LocationFilter';
import WAFilterDnD from './WAFilterDnD';
import { hexToRgb, getWAColor } from './weeklyShiftsUtils';
import styles from './WeeklyShifts.module.css';

interface LocationFilterWrapperProps {
  uniqueLocations: { id: number; name: string }[];
  selectedLocations: number[];
  setSelectedLocations: (v: number[]) => void;
}

export function LocationFilterWrapper({
  uniqueLocations,
  selectedLocations,
  setSelectedLocations,
}: LocationFilterWrapperProps) {
  return (
    <LocationFilter
      locations={uniqueLocations}
      selectedLocations={selectedLocations}
      onSelectedLocationsChange={setSelectedLocations}
    />
  );
}

interface WAFilterDNDWrapperProps {
  draggableWAList: any[];
  hiddenWorkAreas: number[];
  setHiddenWorkAreas: (updater: (prev: number[]) => number[]) => void;
  handleWADragEnd: (result: any) => void;
  selectedLocations: number[];
  isManager: boolean;
  workAreaIds: number[];
}

export function WAFilterDNDWrapper({
  draggableWAList,
  hiddenWorkAreas,
  setHiddenWorkAreas,
  handleWADragEnd,
  selectedLocations,
  isManager,
  workAreaIds,
}: WAFilterDNDWrapperProps) {
  return (
    <WAFilterDnD
      draggableWAList={draggableWAList}
      hiddenWorkAreas={hiddenWorkAreas}
      onHiddenWorkAreasChange={setHiddenWorkAreas}
      onDragEnd={handleWADragEnd}
      selectedLocations={selectedLocations}
      isManager={isManager}
      workAreaIds={workAreaIds}
    />
  );
}

interface WAFilterPillsProps {
  workAreaOptions: any[];
  selectedWAs: number[];
  onToggle: (waId: number) => void;
}

function WAFilterPills({ workAreaOptions, selectedWAs, onToggle }: WAFilterPillsProps) {
  return (
    <div className={styles.waPillContainer}>
      {workAreaOptions.map((wa: any, index: number) => {
        const isSel = selectedWAs.includes(wa.id);
        const waColor = getWAColor(wa.color || '#5271A5');
        const rgb = hexToRgb(waColor);

        return (
          <div
            key={wa.id}
            role="button"
            tabIndex={0}
            onClick={() => onToggle(wa.id)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') onToggle(wa.id);
            }}
            className={`${styles.waPill} ${isSel ? styles.waPillSelected : ''}`}
            style={{
              background: isSel ? waColor : undefined,
              marginLeft: index > 0 ? '2px' : '0',
            }}
          >
            {wa.name}
          </div>
        );
      })}
    </div>
  );
}

interface TTWAFilterRowProps {
  workAreaOptions: any[];
  ttSelectedWAs: number[];
  setTtSelectedWAs: React.Dispatch<React.SetStateAction<number[]>>;
  setTtExportOpen: (v: boolean) => void;
  setTtImportOpen: (v: boolean) => void;
  setTtAddOpen: (v: boolean) => void;
}

export function TTWAFilterRow({
  workAreaOptions,
  ttSelectedWAs,
  setTtSelectedWAs,
  setTtExportOpen,
  setTtImportOpen,
  setTtAddOpen,
}: TTWAFilterRowProps) {
  const handleToggle = (waId: number) => {
    if (ttSelectedWAs.includes(waId)) {
      setTtSelectedWAs(prev => prev.filter(x => x !== waId));
    } else {
      setTtSelectedWAs(prev => [...prev, waId]);
    }
  };

  return (
    <div className={styles.filterRow}>
      <WAFilterPills
        workAreaOptions={workAreaOptions}
        selectedWAs={ttSelectedWAs}
        onToggle={handleToggle}
      />
      <div className={styles.rightActions}>
        <button
          onClick={() => setTtExportOpen(true)}
          title="Export Time Trackings"
          className={styles.circleBtn}
        >
          <FaDownload size={14} />
        </button>
        <button
          data-testid="tt-import-button"
          onClick={() => setTtImportOpen(true)}
          title="Download/Import TT Form"
          className={styles.circleBtn}
        >
          <FaUpload size={14} />
        </button>
        <button
          data-testid="tt-add-button"
          onClick={() => setTtAddOpen(true)}
          className={styles.addPrimaryBtn}
        >
          <FaPlus className={styles.iconXSmall} />
          TimeTracking
        </button>
      </div>
    </div>
  );
}

interface AbsenceWAFilterRowProps {
  workAreaOptions: any[];
  absenceSelectedWAs: number[];
  setAbsenceSelectedWAs: (v: number[]) => void;
  setAbsenceImportOpen: (v: boolean) => void;
  setAbsenceAddOpen: (v: boolean) => void;
}

export function AbsenceWAFilterRow({
  workAreaOptions,
  absenceSelectedWAs,
  setAbsenceSelectedWAs,
  setAbsenceImportOpen,
  setAbsenceAddOpen,
}: AbsenceWAFilterRowProps) {
  const handleToggle = (waId: number) => {
    if (absenceSelectedWAs.includes(waId)) {
      setAbsenceSelectedWAs(absenceSelectedWAs.filter(id => id !== waId));
    } else {
      setAbsenceSelectedWAs([...absenceSelectedWAs, waId]);
    }
  };

  return (
    <div className={styles.filterRow}>
      <WAFilterPills
        workAreaOptions={workAreaOptions}
        selectedWAs={absenceSelectedWAs}
        onToggle={handleToggle}
      />
      <div className={styles.rightActionsOffset}>
        <button
          onClick={() => {}}
          title="Download Absences Template"
          className={styles.circleBtn}
        >
          <FaDownload size={14} />
        </button>
        <button
          onClick={() => setAbsenceImportOpen(true)}
          title="Upload Absences"
          className={styles.circleBtn}
        >
          <FaUpload size={14} />
        </button>
        <button
          data-testid="add-absence-button"
          onClick={() => setAbsenceAddOpen(true)}
          className={styles.addPrimaryBtn}
        >
          <FaPlus className={styles.iconXSmall} />
          Absence
        </button>
      </div>
    </div>
  );
}
